import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

export interface ContextMenuItem {
  label: string;
  icon?: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  /// Muted trailing text. Carries the reason an item is unavailable, which a
  /// hover tooltip can't do inside a menu you're already hovering.
  hint?: string;
  danger?: boolean;
  divider?: boolean;
  /// Nested items in their own panel beside this one, the way the details
  /// panel's Export button opens its own menu. The row toggles that panel
  /// instead of running `onClick`.
  submenu?: ContextMenuItem[];
}

export type ContextMenuCloseReason =
  "select" | "escape" | "outside" | "dismiss";

interface ContextMenuProps {
  items: ContextMenuItem[];
  position: { x: number; y: number } | null;
  onClose: (reason: ContextMenuCloseReason) => void;
  /// Names the menu for screen readers, which otherwise announce it unlabelled —
  /// "Actions for Markets", not "Actions".
  ariaLabel: string;
}

/** Gap kept between a menu panel and the viewport edge. */
const MARGIN = 8;

/// Only the rows of `panel` itself — an open submenu's rows sit inside it in the
/// DOM but navigate on their own.
const rowsOf = (panel: HTMLElement): HTMLElement[] =>
  Array.from(panel.querySelectorAll<HTMLElement>('[role="menuitem"]')).filter(
    (el) => el.closest('[role="menu"]') === panel,
  );

/// Wraps at both ends, and enters from whichever end the arrow key came from.
/// Disabled rows stay in the cycle: skipping them hides that they exist.
const moveFocus = (panel: HTMLElement, delta: number): void => {
  const rows = rowsOf(panel);
  if (rows.length === 0) return;
  const current = rows.indexOf(document.activeElement as HTMLElement);
  const next =
    current === -1
      ? delta > 0
        ? 0
        : rows.length - 1
      : (current + delta + rows.length) % rows.length;
  rows[next].focus();
};

/// True if the panel handled the key itself.
const handleRowNavigation = (
  event: React.KeyboardEvent,
  panel: HTMLElement,
): boolean => {
  const rows = rowsOf(panel);
  switch (event.key) {
    case "ArrowDown":
      moveFocus(panel, 1);
      return true;
    case "ArrowUp":
      moveFocus(panel, -1);
      return true;
    case "Home":
      rows[0]?.focus();
      return true;
    case "End":
      rows[rows.length - 1]?.focus();
      return true;
    default:
      return false;
  }
};

const MenuRow: React.FC<{
  item: ContextMenuItem;
  index: number;
  active?: boolean;
  onSelect: () => void;
  onPointerEnter: () => void;
}> = ({ item, index, active = false, onSelect, onPointerEnter }) => (
  <button
    type="button"
    role="menuitem"
    tabIndex={-1}
    data-index={index}
    // `aria-disabled` rather than `disabled`: an unavailable action stays
    // focusable, so arrowing through the menu still shows it exists.
    aria-disabled={item.disabled || undefined}
    aria-haspopup={item.submenu ? "menu" : undefined}
    aria-expanded={item.submenu ? active : undefined}
    onClick={item.disabled ? undefined : onSelect}
    onPointerEnter={onPointerEnter}
    className={`w-full flex items-center px-3 py-2 text-left ${
      item.disabled
        ? "text-fg-muted-medium cursor-default opacity-40"
        : item.danger
          ? "text-error-regular hover:bg-list-hover cursor-pointer"
          : "text-fg-muted-regular hover:bg-list-hover cursor-pointer"
    } ${active ? "bg-list-hover" : ""}`}
  >
    {item.icon && <div className="mr-2 flex items-center">{item.icon}</div>}
    <span className="truncate grow">{item.label}</span>
    {item.hint && (
      <span className="text-fg-muted-regular-small ml-3 shrink-0">
        {item.hint}
      </span>
    )}
    {item.submenu && <ChevronRightIcon className="icon-sm ml-2 shrink-0" />}
  </button>
);

const MenuDivider: React.FC = () => (
  <div role="separator" className="border-t border-border my-1" />
);

/// Opens to the item's right, flipping to its left and shifting up only as far
/// as the viewport demands. Rendered inside the parent panel, so the parent's
/// click-outside check still counts it as inside the menu.
const ContextSubmenu: React.FC<{
  items: ContextMenuItem[];
  label: string;
  autoFocus: boolean;
  onClose: (reason: ContextMenuCloseReason) => void;
  onDismiss: () => void;
}> = ({ items, label, autoFocus, onClose, onDismiss }) => {
  const ref = useRef<HTMLDivElement>(null);
  const [placed, setPlaced] = useState<{ flip: boolean; shift: number } | null>(
    null,
  );

  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const overflowY = rect.bottom - (window.innerHeight - MARGIN);
    setPlaced({
      flip: rect.right > window.innerWidth - MARGIN,
      shift: Math.max(0, overflowY),
    });
  }, []);

  useEffect(() => {
    const panel = ref.current;
    if (autoFocus && panel) rowsOf(panel)[0]?.focus();
  }, [autoFocus]);

  return (
    <div
      ref={ref}
      role="menu"
      aria-label={label}
      tabIndex={-1}
      className="sidebar-dropdown"
      onKeyDown={(event) => {
        const panel = ref.current;
        if (!panel) return;
        if (handleRowNavigation(event, panel)) {
          event.preventDefault();
          event.stopPropagation();
          return;
        }
        // Left collapses just this panel; Escape at this depth is the parent's
        // to handle, so it closes the whole menu.
        if (event.key === "ArrowLeft") {
          event.preventDefault();
          event.stopPropagation();
          onDismiss();
        }
      }}
      style={{
        position: "absolute",
        top: -(placed?.shift ?? 0),
        left: placed?.flip ? undefined : "100%",
        right: placed?.flip ? "100%" : undefined,
        // Hidden for the frame we measure in, so it can't be seen on the side
        // it's about to flip away from.
        visibility: placed ? "visible" : "hidden",
        zIndex: 1001,
      }}
    >
      {items.map((item, index) =>
        item.divider ? (
          <MenuDivider key={index} />
        ) : (
          <MenuRow
            key={index}
            item={item}
            index={index}
            onSelect={() => {
              item.onClick();
              onClose("select");
            }}
            onPointerEnter={() => {}}
          />
        ),
      )}
    </div>
  );
};

export const ContextMenu: React.FC<ContextMenuProps> = ({
  items,
  position,
  onClose,
  ariaLabel,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [adjusted, setAdjusted] = useState<{ x: number; y: number } | null>(
    null,
  );
  const [openSubmenu, setOpenSubmenu] = useState<number | null>(null);
  /// Only a keyboard open puts focus in the submenu; hovering one shouldn't
  /// pull focus away from the row the pointer is on.
  const [submenuTakesFocus, setSubmenuTakesFocus] = useState(false);

  // Clamp position to viewport after render
  useLayoutEffect(() => {
    if (!position || !menuRef.current) {
      setAdjusted(null);
      return;
    }
    const rect = menuRef.current.getBoundingClientRect();
    const x = Math.min(position.x, window.innerWidth - rect.width - MARGIN);
    const y = Math.min(position.y, window.innerHeight - rect.height - MARGIN);
    setAdjusted({ x: Math.max(MARGIN, x), y: Math.max(MARGIN, y) });
  }, [position]);

  useEffect(() => {
    setOpenSubmenu(null);
    setSubmenuTakesFocus(false);
  }, [position]);

  // Focus the panel rather than its first row: on a right-click open, a
  // pre-highlighted row reads as if the pointer were already on it.
  useEffect(() => {
    if (position) menuRef.current?.focus();
  }, [position]);

  useEffect(() => {
    if (!position) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose("outside");
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose("escape");
    };

    const handleScroll = () => onClose("dismiss");
    const handleResize = () => onClose("dismiss");

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleEscape);
    document.addEventListener("scroll", handleScroll, true);
    window.addEventListener("resize", handleResize);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscape);
      document.removeEventListener("scroll", handleScroll, true);
      window.removeEventListener("resize", handleResize);
    };
  }, [position, onClose]);

  const closeSubmenu = (): void => {
    const parent = menuRef.current?.querySelector<HTMLElement>(
      `[role="menuitem"][data-index="${openSubmenu}"]`,
    );
    setOpenSubmenu(null);
    setSubmenuTakesFocus(false);
    parent?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent): void => {
    const panel = menuRef.current;
    if (!panel) return;
    if (handleRowNavigation(event, panel)) {
      event.preventDefault();
      return;
    }
    if (event.key !== "ArrowRight") return;
    const row = (event.target as HTMLElement).closest("[data-index]");
    const index = Number(row?.getAttribute("data-index"));
    if (!items[index]?.submenu) return;
    event.preventDefault();
    setOpenSubmenu(index);
    setSubmenuTakesFocus(true);
  };

  if (!position) return null;

  const pos = adjusted || position;

  return createPortal(
    <div
      ref={menuRef}
      role="menu"
      aria-label={ariaLabel}
      tabIndex={-1}
      className="sidebar-dropdown"
      onKeyDown={handleKeyDown}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        // Hide the first frame while we measure to clamp into viewport, so the
        // menu doesn't visibly jump from raw click position to clamped one.
        visibility: adjusted ? "visible" : "hidden",
        zIndex: 1000,
      }}
    >
      {items.map((item, index) =>
        item.divider ? (
          <MenuDivider key={index} />
        ) : item.submenu ? (
          <div key={index} className="relative">
            <MenuRow
              item={item}
              index={index}
              active={openSubmenu === index}
              onSelect={() =>
                setOpenSubmenu((cur) => (cur === index ? null : index))
              }
              onPointerEnter={() => setOpenSubmenu(index)}
            />
            {openSubmenu === index && (
              <ContextSubmenu
                items={item.submenu}
                label={item.label}
                autoFocus={submenuTakesFocus}
                onClose={onClose}
                onDismiss={closeSubmenu}
              />
            )}
          </div>
        ) : (
          <MenuRow
            key={index}
            item={item}
            index={index}
            onSelect={() => {
              item.onClick();
              onClose("select");
            }}
            onPointerEnter={() => setOpenSubmenu(null)}
          />
        ),
      )}
    </div>,
    document.body,
  );
};
